import { useEffect, useState } from "react";
import { customerCustomFetch, storeCustomerSessionId } from "@workspace/api-client-react";

// Pedido na mesa pelo QR Code (Ultra). O QR abre /loja/<slug>?mesa=N&t=<código>;
// a vitrine confere no servidor e guarda na ABA: navegar pelo cardápio tira a
// query da URL, e o modo mesa precisa durar até o pedido — e o próximo.
// «1 QR code para cada cliente» (Pedro, 29/10/2026): com a chave ligada, a mesa é
// de um cliente só. Ele entra com nome e WhatsApp antes do cardápio, e outro
// cliente no mesmo QR vê a mesa ocupada até a conta fechar no PDV.
const VALIDADE_MS = 6 * 60 * 60 * 1000;
const chave = (slug: string) => `vnz-mesa-${slug}`;

export interface MesaAtiva {
  mesa: string;
  token: string;
}

export function lerMesa(slug: string): MesaAtiva | null {
  try {
    const raw = sessionStorage.getItem(chave(slug));
    if (!raw) return null;
    const j = JSON.parse(raw);
    if (!j?.mesa || !j?.token || Date.now() - Number(j.at) > VALIDADE_MS) {
      sessionStorage.removeItem(chave(slug));
      return null;
    }
    return { mesa: String(j.mesa), token: String(j.token) };
  } catch {
    return null;
  }
}

function salvarMesa(slug: string, m: MesaAtiva) {
  try {
    sessionStorage.setItem(chave(slug), JSON.stringify({ ...m, at: Date.now() }));
  } catch {
    // Sem storage (aba anônima travada): o modo mesa vale só até sair da página.
  }
}

export function sairDaMesa(slug: string) {
  try {
    sessionStorage.removeItem(chave(slug));
  } catch {
    // idem
  }
}

/** Com o «1 QR code para cada cliente» o QR é uma comanda individual (Pedro, 29/10/2026: nada de «mesa» nesse modo).
 *  As duas palavras são femininas: «a mesa 6» / «a comanda 6». */
export function nomeDaMesa(porCliente: boolean | null | undefined): "mesa" | "comanda" {
  return porCliente ? "comanda" : "mesa";
}

export function NomeDaMesa(porCliente: boolean | null | undefined): "Mesa" | "Comanda" {
  return porCliente ? "Comanda" : "Mesa";
}

const codigoDoErro = (e: unknown): string | undefined => (e as { data?: { error?: { code?: string } } })?.data?.error?.code;

/** «conferindo»: esperando o servidor; «livre»: a chave está desligada ou a mesa não é de ninguém; «minha»: a mesa é
 *  deste cliente neste celular; «ocupada»: está com outro cliente ou outro celular; «esperando»: o PDV cadastrou o
 *  cliente e a comanda espera o primeiro celular (este entra como ele). */
export type DonoDaMesa = "conferindo" | "livre" | "minha" | "ocupada" | "esperando";

/** Mesa do QR na vitrine: confere ?mesa=&t= no servidor e tira da URL (copiar o
 *  link do navegador não espalha o código da mesa). `customerId`: o cliente logado
 *  (undefined enquanto o login carrega). `aoEntrar`: recarrega o cliente logado quando
 *  o celular entra numa comanda cadastrada no PDV (o servidor abre a sessão dele). */
export function useMesa(slug: string, customerId: string | null | undefined, aoEntrar?: () => Promise<void>) {
  const [mesa, setMesa] = useState<MesaAtiva | null>(() => (slug ? lerMesa(slug) : null));
  const [invalida, setInvalida] = useState(false);
  const [porCliente, setPorCliente] = useState(false);
  const [dono, setDono] = useState<DonoDaMesa>("conferindo");
  // A mesa onde o cliente da comanda está (null = ainda não disse: a vitrine pergunta antes do cardápio).
  const [local, setLocal] = useState<string | null>(null);
  // A primeira conferência (do QR da URL ou da mesa guardada) ainda não voltou: a loja que só aceita o QR espera por ela.
  const [conferindo, setConferindo] = useState(true);

  // A mesa guardada na aba também confere a cada visita: a loja pode ter ligado o «1 QR code para cada cliente» ou
  // fechado a conta no PDV desde a última.
  useEffect(() => {
    if (!slug) return;
    const url = new URL(window.location.href);
    const n = url.searchParams.get("mesa");
    const t = url.searchParams.get("t");
    const doQr = !!(n && t);
    if (doQr) {
      url.searchParams.delete("mesa");
      url.searchParams.delete("t");
      window.history.replaceState(window.history.state, "", `${url.pathname}${url.search}${url.hash}`);
    }
    const alvo = doQr ? { mesa: n!, token: t! } : lerMesa(slug);
    if (!alvo) {
      setMesa(null);
      setConferindo(false);
      return;
    }
    let vivo = true;
    customerCustomFetch<{ mesa: string; porCliente?: boolean; ocupada?: boolean; minha?: boolean; esperando?: boolean; local?: string | null }>(
      `/api/catalog/${slug}/mesa?mesa=${encodeURIComponent(alvo.mesa)}&t=${encodeURIComponent(alvo.token)}`,
    )
      .then((r) => {
        if (!vivo) return;
        const m = { mesa: r.mesa, token: alvo.token };
        salvarMesa(slug, m);
        setMesa(m);
        setInvalida(false);
        setPorCliente(r.porCliente === true);
        setLocal(r.local ?? null);
        setDono(r.esperando ? "esperando" : r.ocupada ? "ocupada" : r.minha ? "minha" : "livre");
        setConferindo(false);
      })
      .catch((e) => {
        if (!vivo) return;
        // Sem rede, a mesa guardada fica: o pedido confere de novo no servidor.
        setConferindo(false);
        if (!doQr && codigoDoErro(e) !== "MESA_INVALIDA") {
          setDono("livre");
          return;
        }
        sairDaMesa(slug);
        setMesa(null);
        setInvalida(true);
      });
    return () => {
      vivo = false;
    };
  }, [slug]);

  // Chave ligada: assim que o cliente entra (nome e WhatsApp), a mesa fica presa a ele neste celular, antes de qualquer
  // pedido. Sem login, vale o que a abertura disse: a comanda que está com alguém já abre bloqueada (Pedro, 29/10/2026),
  // e a cadastrada no PDV («esperando») entra já logada como o cliente.
  const esperando = dono === "esperando";
  useEffect(() => {
    if (!slug || !mesa || !porCliente || customerId === undefined) return;
    if (!customerId && !esperando) return;
    let vivo = true;
    customerCustomFetch<{ mesa: string; entrou?: boolean; sessionId?: string; local?: string | null }>(`/api/catalog/${slug}/mesa/entrar`, {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ mesa: mesa.mesa, t: mesa.token }),
    })
      .then(async (r) => {
        if (!vivo) return;
        if (r.entrou) {
          if (r.sessionId) storeCustomerSessionId(r.sessionId);
          await aoEntrar?.();
        }
        if (!vivo) return;
        setLocal(r.local ?? null);
        setDono("minha");
      })
      .catch((e) => {
        if (!vivo) return;
        const code = codigoDoErro(e);
        if (code === "MESA_OCUPADA") setDono("ocupada");
        else if (code === "MESA_INVALIDA") {
          sairDaMesa(slug);
          setMesa(null);
          setInvalida(true);
          // Sem rede o cardápio abre: o pedido confere a mesa de novo no servidor (409 → mesa ocupada).
        } else setDono("minha");
      });
    return () => {
      vivo = false;
    };
  }, [slug, mesa?.mesa, mesa?.token, porCliente, customerId, esperando]);

  return {
    mesa,
    invalida,
    porCliente,
    dono,
    /** A primeira conferência da mesa (do QR ou da guardada) ainda não voltou. */
    conferindo,
    /** «Pedir minha conta»: aviso no app e na impressora da loja. */
    pedirConta: () =>
      customerCustomFetch<{ ok: boolean; impresso?: boolean }>(`/api/catalog/${slug}/mesa/conta`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mesa: mesa?.mesa, t: mesa?.token }),
      }),
    ocupou: () => setDono("ocupada"),
    /** A mesa onde o cliente está (comanda individual). */
    local,
    /** «Em qual mesa você está?»: grava na comanda (aparece no PDV). */
    definirLocal: async (n: string) => {
      const r = await customerCustomFetch<{ local: string }>(`/api/catalog/${slug}/mesa/local`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ mesa: mesa?.mesa, t: mesa?.token, local: n }),
      });
      setLocal(r.local);
    },
  };
}
